import { appOn, assistantTurns, type ChatMessage, type Content, type QueravelMark } from "@rhema/client";
import {
  ArrowUpIcon,
  Button,
  cn,
  EmptyState,
  focusRing,
  IconButton,
  MessageBubble,
  Select,
  XIcon,
} from "@rhema/ui";
import { type FormEvent, Fragment, type ReactNode, useCallback, useMemo, useRef, useState } from "react";
import { firstName } from "../data/view";
import { LinkForm } from "../links/LinkForm";
import { useLinkStatus } from "../links/useLinks";
import { useClient, useClientState } from "../session/session";
import {
  type AskRequest,
  count,
  LANGUAGES,
  labelFor,
  lineOf,
  nameOf,
  promptFor,
  recentMessages,
  sendableMessages,
} from "./ask";
import { useQueravelModels } from "./models";
import { replyKey, useQueravelPanel } from "./panel";
import { StoredTurns } from "./QueravelChat";
import { QueravelHistory, useQueravelConversations } from "./QueravelConversations";
import {
  MAY_SEARCH,
  PRIVACY,
  PrivacyNotice,
  Proposals,
  QueravelAnswer,
  QueravelTile,
  SessionProblem,
} from "./QueravelParts";
import { keepTurns, useAssistantChat } from "./useAssistantChat";
import { type CompletedTurn, useQueravel } from "./useQueravel";

const QUICK: { task: AskRequest["task"]; title: string }[] = [
  { task: "summarise", title: "Summarise" },
  { task: "reply", title: "Draft a reply" },
  { task: "tasks", title: "Tasks and dates" },
];

const chipButton = cn(
  "inline-flex h-[34px] flex-none cursor-pointer items-center rounded-full border border-line bg-linen px-3 text-sm text-ink hover:bg-surface-header",
  focusRing,
);

/**
 * The one Queravel panel. Beside a chat it reads that chat, and the person sees exactly which
 * messages are sent before they go. Anywhere else it is the person's own assistant. Every
 * conversation is kept in the person's Queravel chat, labelled with the chat it was about.
 */
export function QueravelPanel({ variant }: { variant: "aside" | "phone" }) {
  const panel = useQueravelPanel();
  const client = useClient();
  const state = useClientState();
  const desktop = variant === "aside";
  const { status: link, enabled, reload } = useLinkStatus("queravel");
  const offered = enabled === true && link !== null;
  const linked = Boolean(link?.linked);
  const { chatId: assistantId, error: chatError } = useAssistantChat(offered);
  const { messages, conversations } = useQueravelConversations(assistantId);
  const conversation = panel.conversation ?? conversations[0]?.id ?? panel.fallback;
  const current = conversations.find((c) => c.id === conversation);

  // The chat the panel reads, when Queravel is allowed there and the person has not said otherwise.
  const about = panel.about;
  const aboutChat = about ? state.chats[about.chatId] : undefined;
  const allowed = aboutChat ? appOn(state, aboutChat, "queravel") : false;
  const readable = about && aboutChat && allowed && !panel.alone ? about : null;
  const group = aboutChat?.kind === "group" || aboutChat?.kind === "channel";
  const meId = state.me?.id;
  const meName = state.me?.display_name ?? "";
  const chatMessages = about ? state.messages[about.chatId] : undefined;
  const pool = useMemo<ChatMessage[]>(
    () => (readable ? (readable.messages ?? recentMessages(chatMessages ?? [])) : []),
    [readable, chatMessages],
  );
  // A chat with no messages has nothing to read, so the panel is the private assistant there.
  const reading = readable && (readable.messages || pool.length) ? readable : null;
  const nothingToRead = Boolean(readable && !reading);
  const { sendable, leftOut } = useMemo(() => sendableMessages(pool), [pool]);

  const [draft, setDraft] = useState("");
  const [language, setLanguage] = useState("English");
  const [changing, setChanging] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  // Messages wait here until the person confirms. What they confirm is exactly what is sent.
  const [pending, setPending] = useState<{ request: AskRequest; messages: ChatMessage[] } | null>(null);
  // Messages already sent in each conversation. A follow-up question sends only what is new.
  const sent = useRef(new Map<string, Set<string>>());
  // What each chat was called when it was asked about, for the label on the kept conversation.
  const titles = useRef(new Map<string, string>());

  const history = useCallback(() => {
    if (!assistantId) return [];
    return assistantTurns(client.getState().messages[assistantId] ?? [], conversation);
  }, [client, assistantId, conversation]);

  const onComplete = useCallback(
    (turn: CompletedTurn) => {
      if (!assistantId) return;
      const contents: Content[] = [];
      if (turn.question) {
        const mark: QueravelMark = {
          chat_id: conversation,
          ...(turn.label ? { label: turn.label } : {}),
          ...(turn.about ? { source: { chat: turn.about, name: titles.current.get(turn.about) ?? "" } } : {}),
        };
        contents.push({ type: "text", body: turn.question, queravel: mark });
      }
      const files = turn.artifacts.flatMap((a) =>
        a.url ? [{ name: a.name, url: a.url, ...(a.size !== undefined ? { size: a.size } : {}) }] : [],
      );
      // A turn that ends by proposing an action can bring files without any words.
      if (turn.answer || files.length) {
        contents.push({
          type: "queravel",
          body: turn.answer,
          chat_id: conversation,
          ...(turn.references.length ? { references: turn.references } : {}),
          ...(files.length ? { artifacts: files } : {}),
          ...(turn.tools.length ? { tools: turn.tools } : {}),
        });
      }
      keepTurns(client, assistantId, contents);
    },
    [client, assistantId, conversation],
  );

  const picker = useQueravelModels(linked);
  const session = useQueravel({
    chatId: conversation,
    history,
    onComplete,
    onCancelled: (question) => setDraft((d) => d || question),
    model: picker.send,
  });
  const streaming = session.status === "streaming";
  const sentHere = sent.current.get(conversation) ?? new Set<string>();
  const unsent = sendable.filter((m) => !sentHere.has(m.id));

  /** Sends a request about the chat being read, with the messages the person confirmed. */
  const go = (request: AskRequest, list: ChatMessage[]) => {
    if (!reading || !aboutChat) return;
    const prompt = promptFor(request, aboutChat, reading.title, list, meId);
    const label = labelFor(request, list.length, reading.title);
    titles.current.set(reading.chatId, reading.title);
    if (!session.ask(prompt, label === prompt ? undefined : label, reading.chatId)) return;
    panel.noteSource(conversation, reading.chatId);
    sent.current.set(conversation, new Set([...sentHere, ...list.map((m) => m.id)]));
    setPending(null);
    // Everyone in a group can see that Queravel was asked. The notice is encrypted like any message.
    if (group && list.length)
      void client
        .send(
          aboutChat.id,
          { type: "notice", event: "queravel_asked" },
          ...(reading.threadRoot ? [{ threadRoot: reading.threadRoot }] : []),
        )
        .catch(() => undefined);
  };

  /** Asks about the chat. Messages not sent yet wait for the person to confirm them. */
  const request = (req: AskRequest) => {
    if (unsent.length) setPending({ request: req, messages: unsent });
    else go(req, []);
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const text = draft.trim();
    if (!text) return;
    if (reading) {
      request({ task: "question", question: text });
      setDraft("");
      return;
    }
    if (session.ask(text)) {
      panel.noteSource(conversation, null);
      setDraft("");
    }
  };

  const startNew = () => {
    session.dismiss();
    setPending(null);
    panel.startNew();
  };

  const replyWith = (text: string) => {
    if (!about) return;
    panel.handOff(replyKey(about.chatId, about.threadRoot), text);
    // Beside a chat the composer is in view. A thread or a phone screen comes back into view.
    if (!desktop || about.threadRoot) panel.close();
  };

  const copy = (text: string) => {
    void navigator.clipboard
      ?.writeText(text)
      .then(() => setCopied(text))
      .catch(() => undefined);
  };

  const actions = (text: string) =>
    text ? (
      <div className="flex flex-wrap gap-2">
        {about && (
          <Button size="sm" variant="outline" className="text-blue" onClick={() => replyWith(text)}>
            Use as reply
          </Button>
        )}
        <Button size="sm" variant="outline" onClick={() => copy(text)}>
          {copied === text ? "Copied" : "Copy"}
        </Button>
      </div>
    ) : null;

  const header = (
    <header className="flex h-16 flex-none items-center gap-2 border-b border-line pr-3 pl-5">
      <QueravelTile />
      <h2 className="m-0 min-w-0 flex-1 truncate font-serif text-[22px] font-semibold">Queravel</h2>
      {offered && linked && (
        <>
          <Button
            size="sm"
            variant="outline"
            aria-pressed={panel.view === "history"}
            className={cn(panel.view === "history" && "border-queravel bg-queravel-tint text-queravel")}
            onClick={() => panel.setView(panel.view === "history" ? "chat" : "history")}
          >
            History
          </Button>
          <Button size="sm" variant="outline" aria-label="New conversation" onClick={startNew}>
            New
          </Button>
        </>
      )}
      <IconButton label="Close Queravel" variant="ghost" size={38} onClick={panel.close}>
        <XIcon size={18} />
      </IconButton>
    </header>
  );

  const shell = (body: ReactNode) => (
    <section aria-label="Queravel" className="flex min-h-0 flex-1 flex-col bg-surface">
      {header}
      {body}
    </section>
  );

  if (enabled === false || (enabled && !link)) {
    return shell(
      <EmptyState icon={<QueravelTile size={44} />} title="Queravel is not available" className="flex-1">
        Connected apps are turned off on this server.
      </EmptyState>,
    );
  }

  if (panel.view === "history" && linked) {
    return shell(
      <QueravelHistory
        assistantId={assistantId}
        selectedId={current?.id ?? null}
        onOpen={(id) => {
          setPending(null);
          panel.pick(id);
        }}
      />,
    );
  }

  const live = session.live;
  const chip = reading
    ? reading.messages
      ? `${reading.threadRoot ? `Thread in ${reading.title}` : reading.title} · ${count(sendable.length)}`
      : sendable.length
        ? `${reading.title} · last ${count(sendable.length)}`
        : `${reading.title} · nothing to send`
    : null;

  const context =
    about && aboutChat && !allowed ? (
      <p role="status" className="m-0 text-[13.5px] leading-snug text-ink-secondary">
        Queravel is turned off in {about.title}. You are asking your own assistant, and it reads nothing from
        that chat.
      </p>
    ) : about && aboutChat && !nothingToRead ? (
      <>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[13px] text-ink-secondary">Reading</span>
          <span className="max-w-full truncate rounded-full bg-queravel-tint px-2.5 py-1 text-sm font-semibold text-queravel">
            {chip ?? "Nothing, just me"}
          </span>
          <button
            type="button"
            aria-expanded={changing}
            onClick={() => setChanging((c) => !c)}
            className={cn(
              "cursor-pointer border-0 bg-transparent p-0 text-[13px] text-blue underline",
              focusRing,
            )}
          >
            Change
          </button>
        </div>
        {changing && (
          <fieldset aria-label="What Queravel reads" className="m-0 flex flex-wrap gap-2 border-0 p-0">
            {[
              { alone: false, label: about.title },
              { alone: true, label: "Nothing, just me" },
            ].map((o) => (
              <button
                key={o.label}
                type="button"
                aria-pressed={panel.alone === o.alone}
                onClick={() => {
                  panel.setAlone(o.alone);
                  setPending(null);
                  setChanging(false);
                }}
                className={cn(
                  chipButton,
                  panel.alone === o.alone && "border-queravel bg-queravel-tint font-semibold text-queravel",
                )}
              >
                {o.label}
              </button>
            ))}
          </fieldset>
        )}
        {reading && linked && sendable.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            {QUICK.map((q) => (
              <button
                key={q.task}
                type="button"
                className={chipButton}
                onClick={() => request({ task: q.task })}
              >
                {q.title}
              </button>
            ))}
            <span className="inline-flex items-center gap-1">
              <button
                type="button"
                className={chipButton}
                onClick={() => request({ task: "translate", language })}
              >
                Translate
              </button>
              <Select
                label="Language"
                hideLabel
                value={language}
                options={LANGUAGES.map((l) => ({ value: l, label: l }))}
                onChange={setLanguage}
                className="h-[34px] min-h-0 flex-none rounded-full text-sm"
                menuClassName="w-[200px]"
              />
            </span>
          </div>
        )}
      </>
    ) : null;

  const confirm = pending && reading && (
    <section
      aria-label="Check what is sent"
      className="flex flex-col gap-2 rounded-tile border border-line bg-surface p-3.5"
    >
      <h3 className="m-0 text-[15px] font-bold">Send {count(pending.messages.length)} to Queravel?</h3>
      <ul
        aria-label="Messages to send"
        className="m-0 flex max-h-[34vh] list-none flex-col gap-1.5 overflow-y-auto rounded-field bg-linen p-3"
      >
        {pending.messages.map((m) => (
          <li key={m.id} className="text-sm">
            <span className="font-semibold">{aboutChat ? nameOf(aboutChat, m.senderId, meId) : ""}: </span>
            <span className="whitespace-pre-wrap">{lineOf(m)}</span>
          </li>
        ))}
      </ul>
      {leftOut > 0 && (
        <p role="status" className="m-0 text-[13px] text-ink-secondary">
          {leftOut === 1
            ? "1 disappearing message is left out. Messages that disappear are never sent to Queravel."
            : `${leftOut} disappearing messages are left out. Messages that disappear are never sent to Queravel.`}
        </p>
      )}
      <p className="m-0 text-[13px] text-ink-secondary">{PRIVACY}</p>
      <p className="m-0 text-[13px] text-ink-secondary">{MAY_SEARCH}</p>
      {group && (
        <p className="m-0 text-[13px] text-ink-secondary">
          Members of {reading.title} will see "{firstName(meName)} asked Queravel about this{" "}
          {reading.threadRoot ? "thread" : "chat"}".
        </p>
      )}
      <div className="flex gap-2">
        <Button className="flex-[2]" onClick={() => go(pending.request, pending.messages)}>
          Send {count(pending.messages.length)}
        </Button>
        <Button variant="ghost" className="flex-1" onClick={() => setPending(null)}>
          Cancel
        </Button>
      </div>
    </section>
  );

  return shell(
    <>
      {context && (
        <div className="flex flex-none flex-col gap-2.5 border-b border-line px-5 py-3.5">{context}</div>
      )}
      <div className="min-h-0 flex-1 overflow-y-auto">
        <div
          role="log"
          aria-label="Conversation with Queravel"
          className="flex min-h-full flex-col justify-end gap-3 px-5 py-4"
        >
          {chatError && (
            <p role="alert" className="m-0 text-sm text-scarlet">
              {chatError}
            </p>
          )}
          <StoredTurns
            messages={messages}
            conversation={current}
            actions={actions}
            {...(about ? { onReply: replyWith } : {})}
          />
          {live && (
            <Fragment>
              {live.question !== undefined && (
                <MessageBubble direction="out" time="" hideMeta className="max-w-[82%]">
                  {live.label ?? live.question}
                </MessageBubble>
              )}
              <div aria-live="polite" aria-busy={streaming}>
                <QueravelAnswer
                  text={live.text}
                  references={live.references}
                  writing={streaming}
                  step={live.step}
                  tools={live.steps.filter((s) => s.done).map((s) => s.tool)}
                  artifacts={live.artifacts}
                />
              </div>
            </Fragment>
          )}
          {session.queued.map((q, i) => (
            <MessageBubble
              // biome-ignore lint/suspicious/noArrayIndexKey: queued questions keep their order
              key={i}
              direction="out"
              time="Waiting"
              hideMeta
              className="max-w-[82%] opacity-70"
            >
              {q.label ?? q.message}
            </MessageBubble>
          ))}
          <Proposals proposals={session.proposals} session={session} />
          <SessionProblem session={session} />
          {!linked && link && (
            <div className="rounded-tile border border-line bg-surface px-4 py-3.5">
              <p className="m-0 mb-2 text-[15px] font-semibold">Link Queravel to start</p>
              <LinkForm product="queravel" onDone={() => void reload()} />
            </div>
          )}
          {linked && !current && !live && !pending && (
            <EmptyState
              icon={<QueravelTile size={44} />}
              title="Ask Queravel anything"
              action={<PrivacyNotice className="max-w-sm text-left" />}
            >
              {reading
                ? `Ask about ${reading.title}, or pick one of the actions above.`
                : "Your questions and Queravel's answers are kept on your devices."}
            </EmptyState>
          )}
          {confirm}
        </div>
      </div>

      {linked && (
        <div className="flex flex-none flex-col gap-2 border-t border-line px-4 pt-3 pb-[max(16px,env(safe-area-inset-bottom))]">
          <form onSubmit={submit} className="flex items-center gap-2">
            <label className="flex h-12 min-w-0 flex-1 items-center rounded-[14px] border border-line-strong bg-surface px-3.5 focus-within:border-queravel">
              <input
                aria-label="Message Queravel"
                placeholder={reading ? `Ask about ${reading.title}` : "Ask Queravel"}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                className="min-w-0 flex-1 border-0 bg-transparent font-sans text-base text-ink outline-none placeholder:text-ink-meta"
              />
            </label>
            {streaming && !draft.trim() ? (
              <button
                type="button"
                aria-label="Stop"
                onClick={session.stop}
                className={cn(
                  "flex h-12 w-12 flex-none cursor-pointer items-center justify-center rounded-full bg-surface-header text-ink",
                  focusRing,
                )}
              >
                <span aria-hidden="true" className="h-3.5 w-3.5 rounded-[3px] bg-blue" />
              </button>
            ) : (
              <button
                type="submit"
                aria-label="Send to Queravel"
                disabled={!draft.trim()}
                className={cn(
                  "flex h-12 w-12 flex-none cursor-pointer items-center justify-center rounded-full bg-blue text-white disabled:cursor-default disabled:opacity-60",
                  focusRing,
                )}
              >
                <ArrowUpIcon size={19} strokeWidth={2.4} />
              </button>
            )}
          </form>
          {(picker.models.length > 1 || (about && aboutChat && !nothingToRead)) && (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
              {picker.models.length > 1 && (
                <Select
                  label="Model"
                  hideLabel
                  value={picker.selected}
                  options={picker.models.map((m) => ({
                    value: m.id,
                    label: m.name,
                    ...(m.plan ? { detail: "Plan" } : {}),
                  }))}
                  onChange={picker.choose}
                  className="h-[30px] min-h-0 flex-none rounded-full text-[13px]"
                  menuClassName="w-[240px]"
                />
              )}
              {about && aboutChat && !nothingToRead && (
                <p className="m-0 min-w-0 flex-1 text-xs text-ink-secondary">
                  Only you see this conversation.{reading && group ? " Members see that you asked." : ""}
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </>,
  );
}
